import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { articles, getArticleBySlug } from "@/data/articles";
import JsonLd, { SITE_URL, FAVICON_URL } from "@/components/feature/JsonLd";

const MAIN_SITE = "https://kaspreadsheet.com";

export default function Article() {
  const { lang, slug } = useParams();

  const article = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
          Article not found
        </h1>
        <p className="text-foreground-600 mb-6">
          The article you are looking for does not exist. Head back to the homepage to browse our guides.
        </p>
        <Link
          to={`/${lang}`}
          className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition whitespace-nowrap"
        >
          Back to Home
        </Link>
      </div>
    );
  }

  const related = article.related
    .map((r) => articles.find((a) => a.slug === r))
    .filter(Boolean) as typeof articles;

  return (
    <article className="w-full">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          inLanguage: "en",
          datePublished: "2026-01-15T12:00:00+00:00",
          dateModified: "2026-08-22T12:00:00+00:00",
          mainEntityOfPage: `${SITE_URL}/en/article/${article.slug}/`,
          author: { "@type": "Organization", name: "USFansLink" },
          publisher: {
            "@type": "Organization",
            name: "USFansLink",
            logo: { "@type": "ImageObject", url: FAVICON_URL },
          },
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/en/` },
            {
              "@type": "ListItem",
              position: 2,
              name: article.eyebrow,
              item: `${SITE_URL}/en/article/${article.slug}/`,
            },
          ],
        }}
      />
      {/* Breadcrumb + hero */}
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <nav className="text-xs text-foreground-500 mb-5">
            <Link to={`/${lang}`} className="hover:text-foreground-900 transition">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">{article.eyebrow}</span>
          </nav>

          <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold mb-4">
            <i className="ri-book-open-line"></i>
            {article.eyebrow}
          </p>
          <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-foreground-600 text-base leading-relaxed">{article.excerpt}</p>
        </div>
      </div>

      {/* Hero image */}
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-8 md:pt-10">
        <div className="rounded-lg overflow-hidden border border-background-200">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-52 md:h-72 object-cover object-top"
          />
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="space-y-10 text-foreground-800 leading-relaxed">
          {/* Intro */}
          <section>
            {article.intro.map((p, i) => (
              <p key={i} className="text-base md:text-lg">
                {p}
              </p>
            ))}
          </section>

          {article.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                {section.heading}
              </h2>

              {section.body?.map((p, i) => (
                <p key={i} className={i > 0 ? "mt-4" : ""}>
                  {p}
                </p>
              ))}

              {section.list && (
                <ul className="mt-4 space-y-2">
                  {section.list.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <i className="ri-check-line text-primary-600 mt-0.5"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.table && (
                <div className="mt-4 overflow-x-auto rounded-lg border border-background-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100 text-left">
                        {section.table.headers.map((h) => (
                          <th key={h} className="px-4 py-3 font-semibold text-foreground-950 whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.rows.map((row, i) => (
                        <tr key={row[0]} className={i % 2 ? "bg-background-50" : "bg-background-100/50"}>
                          {row.map((cell, ci) => (
                            <td
                              key={ci}
                              className={`px-4 py-3 ${
                                ci === 0 ? "font-medium text-foreground-900 whitespace-nowrap" : "text-foreground-600"
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          {/* FAQ */}
          {article.faqs.length > 0 && (
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {article.faqs.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">{f.q}</h3>
                    <p className="text-sm text-foreground-600">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Internal link back home */}
          <section className="rounded-lg bg-accent-50 border border-accent-200 p-6">
            <p className="text-foreground-700">
              Looking for more? Return to our{" "}
              <Link to={`/${lang}`} className="text-primary-700 font-semibold hover:underline">
                USFans Spreadsheet home page
              </Link>{" "}
              to browse all categories, guides and safety resources in one place.
            </p>
          </section>

          {/* Related articles */}
          {related.length > 0 && (
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/${lang}/article/${r.slug}`}
                    className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition"
                  >
                    <div className="relative h-24 overflow-hidden">
                      <img
                        src={r.image}
                        alt={r.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-primary-700 font-semibold mb-1">{r.eyebrow}</p>
                      <p className="text-sm font-semibold text-foreground-900 leading-snug line-clamp-2">{r.title}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Final CTA */}
          <section className="rounded-lg bg-background-100 border border-background-200 p-6 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
              Ready to Browse the USFans Spreadsheet?
            </h2>
            <p className="text-foreground-600 mb-6">
              Open the full product catalog, compare sellers and find what you need on the main website.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={MAIN_SITE}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto h-11 px-6 inline-flex items-center justify-center rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition whitespace-nowrap"
              >
                Visit Main Website
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
              <Link
                to={`/${lang}`}
                className="w-full sm:w-auto h-11 px-6 inline-flex items-center justify-center rounded-md border border-background-300 text-foreground-900 font-semibold hover:bg-background-200 transition whitespace-nowrap"
              >
                Back to Home
              </Link>
            </div>
          </section>
        </div>
      </div>
    </article>
  );
}